import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import {
  Button,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { InputText } from 'app/components/elements/InputText';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ButtonCustom, ButtonOutLine } from 'app/components/elements/ButtonCustom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AssetModel, AssetModelForm, DropdownOption, MaintenanceConfig } from '../../../type';
import { yupResolver } from '@hookform/resolvers/yup';
import { AssetModelFormSchema } from 'utils/validate';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { InputSelect } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { useInsertOptionManufacturer, useManufactureStore } from 'store/manufacture';
import { useInsertOptionTriggerType, useTriggerTypeStore } from 'store/triggerType';
import {
  normalizeTriggerSubTypeId,
  useInsertOptionTriggerSubType,
  useTriggerSubTypeStore
} from 'store/triggerSubType';
import { getDropdownOptions } from '../../../../../../services/asset';
import { NumericInput } from 'app/components/elements/NumericInput';
import { useNotificationSettingsStore } from 'store/notificationSettings';

// counter source stays hardcoded — it's a boolean concept not from DB
const COUNTER_SOURCE_OPTIONS = [
  { value: 'IOT', label: 'IoT Sensor' },
  { value: 'MANUAL', label: 'Manual Entry' }
];

// Sub Type dropdown only shows up for this trigger type, and unlike other trigger
// types (capped at 1 config each), Runtime allows one config per distinct sub type.
const RUNTIME_TRIGGER_TYPE = 'Runtime';

// ─── Props ───────────────────────────────────────────────────────────────────
type ModalAddNewModelProp = {
  isEdit?: boolean;
  isOpen: boolean;
  initData?: AssetModel;
  onCloseModal: () => void;
  onSubmit: (value: AssetModelForm) => void;
  optionTypes: { label: string; value: string }[];
  handleDeleteCreatedFile?: (idFile: number) => void;
};

export const ModalAddNewModel = ({
  isOpen,
  onCloseModal,
  onSubmit,
  isEdit,
  initData,
  optionTypes,
  handleDeleteCreatedFile
}: ModalAddNewModelProp) => {
  const fileInputIconRef = useRef<HTMLInputElement>(null);
  const savedMaintenanceConfigs = useRef<AssetModelForm['maintenanceConfigs']>([]); // ← add here

  const { defaultNotificationWindow } = useNotificationSettingsStore();

  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm<AssetModelForm>({
    resolver: yupResolver(AssetModelFormSchema),
    defaultValues: {
      oemAttribute: [{ value: '', label: '', isDropdown: false }],
      maintenanceEnabled: false,
      maintenanceConfigs: []
    }
  });
  const { manufacturers } = useManufactureStore();
  const insertOption = useInsertOptionManufacturer();

  // ─── Trigger type store (NEW — mirrors manufacturer exactly) ──────────────
  const { triggerTypes } = useTriggerTypeStore();
  const insertTriggerOption = useInsertOptionTriggerType();

  // ─── Sub type store (NEW — only relevant for the Runtime trigger type) ────
  const { triggerSubTypes: subTypes } = useTriggerSubTypeStore();
  const insertSubTypeOption = useInsertOptionTriggerSubType();

  const watchedMaintenanceConfigs = watch('maintenanceConfigs');

  // Non-Runtime trigger types stay capped at 1 config each. Runtime is capped
  // at 1 config per distinct sub type instead, so it keeps showing up as an
  // option until every sub type has been used by some other row.
  const getAvailableTriggersTypes = (currentIndex: number) => {
    const others = watchedMaintenanceConfigs?.filter((_, i) => i !== currentIndex) ?? [];

    const usedNonRuntimeTypes = others
      .filter((c) => c.maintenanceTriggerType && c.maintenanceTriggerType !== RUNTIME_TRIGGER_TYPE)
      .map((c) => c.maintenanceTriggerType);

    const usedRuntimeSubTypes = others
      .filter((c) => c.maintenanceTriggerType === RUNTIME_TRIGGER_TYPE)
      .map((c) => c.triggerSubTypeId)
      .filter(Boolean);

    const runtimeExhausted = subTypes.length > 0 && usedRuntimeSubTypes.length >= subTypes.length;

    return triggerTypes.filter((t) => {
      if (t.value === RUNTIME_TRIGGER_TYPE) return !runtimeExhausted;
      return !usedNonRuntimeTypes.includes(t.value);
    });
  };

  // Sub types already used by other Runtime rows are excluded so each Runtime
  // config gets a distinct sub type.
  const getAvailableSubTypes = (currentIndex: number) => {
    const usedSubTypes =
      watchedMaintenanceConfigs
        ?.filter((_, i) => i !== currentIndex)
        .filter((c) => c.maintenanceTriggerType === RUNTIME_TRIGGER_TYPE)
        .map((c) => c.triggerSubTypeId)
        .filter(Boolean) ?? [];

    return subTypes.filter((s) => !usedSubTypes.includes(s.value));
  };

  // Max configs = 1 per non-Runtime trigger type + 1 per sub type for Runtime.
  const getMaxMaintenanceConfigs = () => {
    const nonRuntimeTriggerCount = triggerTypes.filter(
      (t) => t.value !== RUNTIME_TRIGGER_TYPE
    ).length;
    const hasRuntimeTriggerType = triggerTypes.some((t) => t.value === RUNTIME_TRIGGER_TYPE);
    const runtimeSlots = hasRuntimeTriggerType ? Math.max(subTypes.length, 1) : 0;

    return nonRuntimeTriggerCount + runtimeSlots;
  };

  const makeEmptyEntry = useCallback(
    (): MaintenanceConfig => ({
      maintenanceTriggerType: '',
      triggerSubTypeId: '',
      frequencyType: '',
      maintenanceFrequency: undefined,
      minMaintenanceFrequency: undefined,
      maxMaintenanceFrequency: undefined,
      notificationWindow: defaultNotificationWindow,
      counterSource: ''
    }),
    [defaultNotificationWindow]
  );

  const listFiles = watch('files');
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    const newFiles = Array.from(fileList);
    setValue('files', listFiles ? [...listFiles, ...newFiles] : newFiles);
  };
  const handleFileClick = useCallback(() => {
    if (fileInputIconRef.current) {
      fileInputIconRef.current.click();
    }
  }, []);

  const handleDeleteFile = useCallback(
    (indexDelete: number) => {
      const newListFile = listFiles?.filter((_, index) => index !== indexDelete);
      setValue('files', newListFile || []);
    },
    [listFiles, setValue]
  );

  useEffect(() => {
    if (!isOpen) {
      savedMaintenanceConfigs.current = []; // ← add this line
      reset({
        name: '',
        manufacture: '',
        otherManufacture: '',
        type: '',
        oemAttribute: [],
        files: [],
        maintenanceEnabled: false,
        maintenanceConfigs: []
      });
      return;
    }

    if (isEdit && initData) {
      insertOption({
        label: initData.manufacturer.name,
        value: initData.manufacturer.id.toString()
      });

      // NEW — force-insert each saved trigger type in case it's not in store yet
      // same reason as manufacturer: store may not have it if list is paginated
      // or if the value was saved before this session's fetch completed
      // both label and value = the name string e.g. "Operating Hours"
      initData.maintenanceConfigs?.forEach((schedule) => {
        if (schedule.maintenanceTriggerType) {
          insertTriggerOption({
            label: schedule.maintenanceTriggerType,
            value: schedule.maintenanceTriggerType
          });
        }
        // NEW — same reason as trigger type: store may not have this sub type yet.
        // GET responses carry both the flat `triggerSubTypeId` and the populated
        // `triggerSubType: { id, name, defaultUnit }` relation — we need the name here.
        const subTypeRef = schedule.triggerSubType as unknown as
          | { id: number; name: string; defaultUnit?: string | null }
          | null
          | undefined;
        if (subTypeRef) {
          insertSubTypeOption({
            label: subTypeRef.name,
            value: String(subTypeRef.id)
          });
        }
      });

      const hasMaintenanceData = !!initData?.maintenanceConfigs?.length;
      reset({
        name: initData.name,
        manufacture: initData.manufacturer.id.toString(),
        otherManufacture: initData.otherManufacture,
        type: initData.type.id.toString(),
        // oemAttribute: initData?.oemAttributes?.length ? initData.oemAttributes : [],
        oemAttribute: initData?.oemAttributes?.length
          ? initData.oemAttributes.map((attr) => ({
              label: attr.label ?? '',
              value: attr.value ?? '',
              isDropdown: attr.isDropdown ?? false,
              dropdownId: attr.dropdownId ?? undefined,
              dropdownValue: attr.dropdownValue ?? undefined
            }))
          : [],
        files: [],
        maintenanceEnabled: hasMaintenanceData,
        // GET responses carry triggerSubTypeId as a plain number — normalize to a
        // string so it matches the Select-bound form field.
        maintenanceConfigs: hasMaintenanceData
          ? (initData.maintenanceConfigs || []).map((c) => ({
              ...c,
              triggerSubTypeId: normalizeTriggerSubTypeId(c.triggerSubTypeId)
            }))
          : []
      });
      return;
    }

    if (!isEdit) {
      reset({
        name: '',
        manufacture: '',
        otherManufacture: '',
        type: '',
        oemAttribute: [{ value: '', label: '', isDropdown: false }],
        files: [],
        maintenanceEnabled: false,
        maintenanceConfigs: []
      });
    }
  }, [reset, initData, insertOption, isOpen, isEdit]);
  const {
    fields: oemAttributeFields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'oemAttribute'
  });

  // State for dropdown options
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);

  // ─── Maintenance field array ──────────────────────────────────────────────
  const {
    fields: maintenanceFields,
    append: appendMaintenance,
    remove: removeMaintenance
  } = useFieldArray({ control, name: 'maintenanceConfigs' });

  // Fetch dropdown options when modal opens
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const response = await getDropdownOptions('ASSET_MODEL');
        if (response.success) {
          setDropdownOptions(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };

    if (isOpen) {
      fetchDropdownOptions();
    }
  }, [isOpen]);

  // Watch OEM attributes for input type changes
  const watchedOemAttributes = watch('oemAttribute');

  // Get dropdown values for a specific dropdown option
  const getDropdownValuesForOption = (dropdownOptionId: number | null | undefined) => {
    if (!dropdownOptionId) return [];
    const option = dropdownOptions.find((opt) => opt.id === Number(dropdownOptionId));
    return option?.dropdownValues || [];
  };

  const watchHasMaintenance = watch('maintenanceEnabled');

  // ─── Auto-manage maintenance groups when checkbox toggles ─────────────────
  useEffect(() => {
    if (!watchHasMaintenance) {
      const current = watch('maintenanceConfigs');

      if (current?.length) {
        savedMaintenanceConfigs.current = current;
      }

      setValue('maintenanceConfigs', []);
    } else if (watchHasMaintenance) {
      if (savedMaintenanceConfigs.current?.length) {
        setValue('maintenanceConfigs', savedMaintenanceConfigs.current);

        savedMaintenanceConfigs.current = [];
      } else if (maintenanceFields.length === 0 && !isEdit) {
        // ↑ guard: don't append empty entry when edit mode is populating the form

        appendMaintenance(makeEmptyEntry());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchHasMaintenance]);

  // ─── Typed error helper (avoids `as any` for maintenance field errors) ────
  type MaintenanceFieldKey =
    | 'maintenanceTriggerType'
    | 'triggerSubTypeId'
    | 'frequencyType'
    | 'maintenanceFrequency'
    | 'minMaintenanceFrequency'
    | 'maxMaintenanceFrequency'
    | 'notificationWindow'
    | 'counterSource';

  const getMaintenanceError = (index: number, field: MaintenanceFieldKey): string => {
    const entry = errors.maintenanceConfigs?.[index];
    if (!entry || !('message' in (entry[field] ?? {}))) return '';
    return (entry[field] as { message?: string })?.message || '';
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <DialogConfirm
      maxWidth={480}
      textTitle={isEdit ? 'Edit Model' : 'Add New Model'}
      isOpen={isOpen}
      onClose={onCloseModal}>
      <Stack width={'100%'} alignItems={'flex-end'}>
        <Stack width={'100%'} flexDirection={'row'} alignItems={'flex-start'} gap={'20px'}>
          <Stack style={{ width: '100%', gap: '20px' }}>
            <InputText
              colorTitle={'#226AAA'}
              inputError={errors.name?.message}
              placeholder={'Enter name'}
              title={'Name'}
              inputProps={{
                ...register('name')
              }}
            />
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSelect
                  colorTitle={'#226AAA'}
                  inputError={errors.manufacture?.message as string}
                  title={'Manufacturer'}
                  value={value || ''}
                  placeholder={'Select Manufacture'}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    const v = String(e.target.value);
                    onChange(v);
                    if (v !== 'OTHER') {
                      setValue('otherManufacture', undefined);
                    }
                  }}
                  sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                  options={manufacturers || []}
                />
              )}
              name={'manufacture'}
            />
            {watch('manufacture') === 'OTHER' && (
              <InputText
                colorTitle={'#226AAA'}
                inputError={errors.otherManufacture?.message}
                placeholder={'Enter other manufacturer'}
                title={'Other Manufacturer'}
                inputProps={{
                  ...register('otherManufacture')
                }}
              />
            )}
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSelect
                  colorTitle={'#226AAA'}
                  inputError={errors.type?.message as string}
                  title={'Type'}
                  value={value || ''}
                  placeholder={'Select Asset Type'}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    onChange(e.target.value as string);
                  }}
                  sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                  options={optionTypes || []}
                />
              )}
              name={'type'}
            />
            <Stack>
              <Typography fontWeight={700} mb={1.25} color={'#226AAA'}>
                Attach PDF
              </Typography>
              <input
                accept="application/pdf"
                ref={fileInputIconRef}
                multiple
                hidden
                onChange={(event) => handleFileChange(event)}
                type="file"
              />
              <WrapperInputFile onClick={handleFileClick}>
                <img
                  style={{ cursor: 'pointer' }}
                  alt={'icon-file'}
                  src={'/icons/icon_add_file.svg'}
                  width={'24px'}
                />
                <TextStyled>Attach Files</TextStyled>
              </WrapperInputFile>
              <Grid container spacing={2}>
                {!!initData?.files.length && (
                  <Grid item xs={6}>
                    <TextStyled>Created Files</TextStyled>
                    {initData?.files?.map((item, index) => {
                      if (item.deletedAt) return;
                      return (
                        <Stack
                          key={index}
                          flexDirection="row"
                          gap={2}
                          alignItems="center"
                          justifyContent={'space-between'}>
                          <Link
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              maxWidth: '155px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'inline-block'
                            }}>
                            {item.storage.fileName}
                          </Link>
                          <img
                            onClick={() =>
                              handleDeleteCreatedFile && handleDeleteCreatedFile(item.id)
                            }
                            alt="icon-user"
                            src="/icons/icon_delete.svg"
                            width={24}
                          />
                        </Stack>
                      );
                    })}
                  </Grid>
                )}
                <Grid item xs={6}>
                  {!!initData?.files.length && <TextStyled>New Files</TextStyled>}
                  {listFiles?.map((item, index) => {
                    const fileURL = URL.createObjectURL(item);
                    return (
                      <Stack
                        key={index}
                        flexDirection="row"
                        gap={2}
                        alignItems="center"
                        justifyContent={'space-between'}>
                        <Link
                          href={fileURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            maxWidth: '155px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'inline-block'
                          }}>
                          {item.name}
                        </Link>
                        <img
                          onClick={() => handleDeleteFile(index)}
                          alt="icon-user"
                          src="/icons/icon_delete.svg"
                          width={24}
                        />
                      </Stack>
                    );
                  })}
                </Grid>
              </Grid>
            </Stack>
            {oemAttributeFields.map((field, index) => {
              const currentIsDropdown = watchedOemAttributes?.[index]?.isDropdown || false;
              const currentDropdownId = watchedOemAttributes?.[index]?.dropdownId;

              return (
                <Stack key={field.id} gap={1} width={'100%'}>
                  <Stack
                    flexDirection={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}>
                    <Typography mb={1.25} fontWeight={700} color={'#226AAA'}>
                      {`OEM Attribute ${index + 1}`}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        remove(index);
                      }}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                  <Stack gap={1} width={'100%'} alignItems={'center'}>
                    <Stack width={'100%'}>
                      <Controller
                        name={`oemAttribute.${index}.label`}
                        control={control}
                        render={({ field }) => {
                          return (
                            <InputText
                              {...field}
                              placeholder="Label"
                              inputError={errors.oemAttribute?.[index]?.label?.message || ''}
                              inputProps={{
                                ...field
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    {/* Input Type Selector */}
                    <Stack width={'100%'}>
                      <Controller
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <InputSelect
                            value={value ? 'true' : 'false'}
                            placeholder="Type"
                            onChange={(e: SelectChangeEvent<unknown>) => {
                              const isDropdown = e.target.value === 'true';
                              onChange(isDropdown);
                              // Clear related fields when type changes
                              if (!isDropdown) {
                                setValue(`oemAttribute.${index}.dropdownId`, undefined);
                                setValue(`oemAttribute.${index}.dropdownValue`, undefined);
                              } else {
                                setValue(`oemAttribute.${index}.value`, '');
                              }
                            }}
                            sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                            options={[
                              { value: 'false', label: 'Text' },
                              { value: 'true', label: 'Dropdown' }
                            ]}
                          />
                        )}
                        name={`oemAttribute.${index}.isDropdown`}
                      />
                    </Stack>

                    {/* Conditional Value Input based on type */}
                    {!currentIsDropdown ? (
                      <Controller
                        name={`oemAttribute.${index}.value`}
                        control={control}
                        render={({ field }) => {
                          return (
                            <InputText
                              {...field}
                              placeholder="Value"
                              inputError={errors.oemAttribute?.[index]?.value?.message || ''}
                              inputProps={{
                                ...field
                              }}
                            />
                          );
                        }}
                      />
                    ) : (
                      <Stack gap={1} width={'100%'}>
                        <Controller
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <InputSelect
                              value={value ? value.toString() : ''}
                              placeholder="Select Dropdown"
                              onChange={(e: SelectChangeEvent<unknown>) => {
                                const val = e.target.value;
                                onChange(val === '' ? null : Number(val));
                                // Reset dropdown value when dropdown option changes
                                setValue(`oemAttribute.${index}.dropdownValue`, null);
                                setValue(`oemAttribute.${index}.value`, '');
                              }}
                              sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                              options={[
                                { value: '', label: 'Select...' },
                                ...dropdownOptions.map((option) => ({
                                  value: option.id.toString(),
                                  label: option.name
                                }))
                              ]}
                            />
                          )}
                          name={`oemAttribute.${index}.dropdownId`}
                        />

                        {/* Dropdown Value Selector */}
                        {currentDropdownId && (
                          <Stack width={'100%'}>
                            <Controller
                              control={control}
                              render={({ field: { onChange, value } }) => {
                                const dropdownValues =
                                  getDropdownValuesForOption(currentDropdownId);
                                return (
                                  <InputSelect
                                    value={value ? value.toString() : ''}
                                    placeholder="Select Value"
                                    onChange={(e: SelectChangeEvent<unknown>) => {
                                      const val = e.target.value;
                                      onChange(val === '' ? null : Number(val));
                                      // Also set displayed value
                                      const selectedOption = dropdownValues.find(
                                        (dv) => dv.id === Number(val)
                                      );
                                      if (selectedOption) {
                                        setValue(
                                          `oemAttribute.${index}.value`,
                                          selectedOption.value || selectedOption.name
                                        );
                                      }
                                    }}
                                    sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                                    options={[
                                      { value: '', label: 'Select...' },
                                      ...dropdownValues.map((dv) => ({
                                        value: dv.id.toString(),
                                        label: dv.value || dv.name
                                      }))
                                    ]}
                                  />
                                );
                              }}
                              name={`oemAttribute.${index}.dropdownValue`}
                            />
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              );
            })}
            <Stack width={'100%'} alignItems={'flex-end'}>
              <ButtonCustom
                label={'Add Another OEM Attribute'}
                style={{ width: '260px' }}
                onClick={() => {
                  append({ value: '', label: '', isDropdown: false });
                }}
              />
            </Stack>

            {/* ── Maintenance Enabled Checkbox ── */}
            <FormControlLabel
              control={
                <Controller
                  control={control}
                  name="maintenanceEnabled"
                  render={({ field: { onChange, value } }) => (
                    <Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />
                  )}
                />
              }
              label={
                <Typography fontWeight={700} color={'#226AAA'}>
                  Enable Maintenance Schedule
                </Typography>
              }
            />
            {/* ── Maintenance Schedule Groups (visible only when checked) ── */}
            {watchHasMaintenance && (
              <Stack width={'100%'} gap={2}>
                {maintenanceFields.map((field, index) => (
                  <Stack
                    key={field.id}
                    gap={1}
                    width={'100%'}
                    sx={{ border: '1px solid #D0D9E4', borderRadius: '10px', p: 2 }}>
                    {/* Group header */}
                    <Stack
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}>
                      <Typography mb={1.25} fontWeight={700} color={'#226AAA'}>
                        {`Maintenance Schedule ${index + 1}`}
                      </Typography>
                      {maintenanceFields.length > 1 && (
                        <IconButton onClick={() => removeMaintenance(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>

                    {/* maintenanceTriggerType — options from store (fetched from API) */}
                    <Controller
                      control={control}
                      name={`maintenanceConfigs.${index}.maintenanceTriggerType`}
                      render={({ field: { onChange, value } }) => (
                        <InputSelect
                          colorTitle={'#226AAA'}
                          title={'Trigger Type'}
                          value={value || ''}
                          placeholder={'Select Trigger Type'}
                          onChange={(e: SelectChangeEvent<unknown>) => {
                            const v = e.target.value as string;
                            onChange(v);
                            if (v !== RUNTIME_TRIGGER_TYPE) {
                              setValue(`maintenanceConfigs.${index}.triggerSubTypeId`, '');
                            }
                          }}
                          sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                          // options={triggerTypes} // from useTriggerTypeStore
                          options={getAvailableTriggersTypes(index)}
                          inputError={getMaintenanceError(index, 'maintenanceTriggerType')}
                        />
                      )}
                    />

                    {/* triggerSubTypeId — only visible for the Runtime trigger type */}
                    {watch(`maintenanceConfigs.${index}.maintenanceTriggerType`) ===
                      RUNTIME_TRIGGER_TYPE && (
                      <Controller
                        control={control}
                        name={`maintenanceConfigs.${index}.triggerSubTypeId`}
                        render={({ field: { onChange, value } }) => (
                          <InputSelect
                            colorTitle={'#226AAA'}
                            title={'Sub Type'}
                            value={value || ''}
                            placeholder={'Select Sub Type'}
                            onChange={(e: SelectChangeEvent<unknown>) =>
                              onChange(e.target.value as string)
                            }
                            sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                            options={getAvailableSubTypes(index)}
                            inputError={getMaintenanceError(index, 'triggerSubTypeId')}
                          />
                        )}
                      />
                    )}

                    {/* frequencyType */}
                    <Controller
                      control={control}
                      name={`maintenanceConfigs.${index}.frequencyType`}
                      render={({ field: { onChange, value } }) => (
                        <InputSelect
                          colorTitle={'#226AAA'}
                          title={'Frequency Type'}
                          value={value || ''}
                          placeholder={'Select Frequency Type'}
                          onChange={(e: SelectChangeEvent<unknown>) =>
                            onChange(e.target.value as string)
                          }
                          sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                          options={[
                            { value: 'INTERVAL', label: 'Interval' },
                            { value: 'RANGE', label: 'Range' }
                          ]}
                          inputError={getMaintenanceError(index, 'frequencyType')}
                        />
                      )}
                    />

                    {/* INTERVAL */}
                    {watch(`maintenanceConfigs.${index}.frequencyType`) === 'INTERVAL' && (
                      <Controller
                        name={`maintenanceConfigs.${index}.maintenanceFrequency`}
                        control={control}
                        render={({ field: { onChange, value, ...rest } }) => (
                          <NumericInput
                            title={'Maintenance Frequency'}
                            placeholder={'e.g. 500'}
                            colorTitle={'#226AAA'}
                            inputError={getMaintenanceError(index, 'maintenanceFrequency')}
                            value={value}
                            onChange={onChange}
                            restProps={rest}
                          />
                        )}
                      />
                    )}

                    {/* RANGE */}
                    {watch(`maintenanceConfigs.${index}.frequencyType`) === 'RANGE' && (
                      <>
                        <Controller
                          name={`maintenanceConfigs.${index}.minMaintenanceFrequency`}
                          control={control}
                          render={({ field: { onChange, value, ...rest } }) => (
                            <NumericInput
                              title={'Min Maintenance Frequency'}
                              placeholder={'e.g. 10'}
                              colorTitle={'#226AAA'}
                              inputError={getMaintenanceError(index, 'minMaintenanceFrequency')}
                              value={value}
                              onChange={onChange}
                              restProps={rest}
                            />
                          )}
                        />
                        <Controller
                          name={`maintenanceConfigs.${index}.maxMaintenanceFrequency`}
                          control={control}
                          render={({ field: { onChange, value, ...rest } }) => (
                            <NumericInput
                              title={'Max Maintenance Frequency'}
                              placeholder={'e.g. 30'}
                              colorTitle={'#226AAA'}
                              inputError={getMaintenanceError(index, 'maxMaintenanceFrequency')}
                              value={value}
                              onChange={onChange}
                              restProps={rest}
                            />
                          )}
                        />
                      </>
                    )}

                    {/* notificationWindow */}
                    <Controller
                      name={`maintenanceConfigs.${index}.notificationWindow`}
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <NumericInput
                          title={'Notification Window'}
                          placeholder={'e.g. 24 (hours before due)'}
                          colorTitle={'#226AAA'}
                          inputError={getMaintenanceError(index, 'notificationWindow')}
                          value={value}
                          onChange={onChange}
                          restProps={rest}
                        />
                      )}
                    />

                    {/* counterSource */}
                    <Controller
                      control={control}
                      name={`maintenanceConfigs.${index}.counterSource`}
                      render={({ field: { onChange, value } }) => (
                        <InputSelect
                          colorTitle={'#226AAA'}
                          title={'Counter Source'}
                          value={value || ''}
                          placeholder={'Select Counter Source'}
                          onChange={(e: SelectChangeEvent<unknown>) =>
                            onChange(e.target.value as string)
                          }
                          sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                          options={COUNTER_SOURCE_OPTIONS}
                          inputError={getMaintenanceError(index, 'counterSource')}
                        />
                      )}
                    />
                  </Stack>
                ))}

                {/* Add another maintenance group */}
                <Stack width={'100%'} alignItems={'flex-end'}>
                  <ButtonCustom
                    label={'Add Another Maintenance Config'}
                    style={{ width: '300px' }}
                    disabled={maintenanceFields.length >= getMaxMaintenanceConfigs()}
                    onClick={() => appendMaintenance(makeEmptyEntry())}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack
          width={'100%'}
          mt={'30px'}
          alignItems={'center'}
          justifyContent={'center'}
          flexDirection={'row'}
          gap={'20px'}>
          <ButtonOutLine
            label={'Cancel'}
            style={{ width: `calc((100% - 20px)/2)` }}
            onClick={onCloseModal}
            fullWidth
          />
          <ButtonCustom
            label={isEdit ? 'Save' : 'Add'}
            style={{ width: `calc((100% - 20px)/2)` }}
            onClick={handleSubmit(onSubmit)}
            fullWidth
          />
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};

const WrapperInputFile = styled(Button)(() => ({
  border: '1px solid #BABAB6',
  borderRadius: '10px',
  padding: '8px 16px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  height: '56px',
  flexDirection: 'row',
  marginBottom: '10px'
}));
const TextStyled = styled(Typography)(() => ({
  color: '#226AAA',
  fontSize: '18px',
  fontWeight: 700
}));
